import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

/** PRD M7 — 한 글자씩 쓰기 60ms → 다 쓰면 1.5s 멈춤 → 한 글자씩 지우기 30ms → 다음 예시(무한). */
const TYPE_INTERVAL_MS = 60;
const HOLD_MS = 1500;
const DELETE_INTERVAL_MS = 30;
/** 다 지운 뒤 다음 예시를 쓰기 전 한 박자. */
const EMPTY_HOLD_MS = 300;

/**
 * 코드 포인트 단위로 쪼갠다 — 이모지·서로게이트 쌍이 반으로 잘리지 않게. 태국어·인도계 결합 기호가
 * 따로 붙어 들어오는 건 실제 키보드 입력 순서와 같아 그대로 둔다.
 */
const toGraphemes = (text: string): string[] => Array.from(text);

type Phase = 'typing' | 'holding' | 'deleting' | 'empty';

/**
 * 주제 카드 입력칸 예시를 키보드로 쓰고 지우듯 돌린다(PRD M7).
 * reduced-motion 이면 첫 예시만 멈춘 채로 둔다. 카드가 화면 밖이면 멈춘다 — 반환한 ref 를 그림에 붙인다.
 */
export const useTopicSampleTyping = (samples: readonly string[]) => {
  const prefersReducedMotion = useReducedMotion();
  const graphemeSamples = useMemo(() => samples.map(toGraphemes), [samples]);
  const [sampleIndex, setSampleIndex] = useState(0);
  const [shownCount, setShownCount] = useState(
    () => graphemeSamples[0]?.length ?? 0,
  );
  const [phase, setPhase] = useState<Phase>('holding');
  const [isInView, setIsInView] = useState(true);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsInView(entry.isIntersecting);
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  const isAnimated =
    !prefersReducedMotion && isInView && graphemeSamples.length > 1;

  useEffect(() => {
    if (!isAnimated) return;
    const current = graphemeSamples[sampleIndex] ?? [];

    const delay = {
      typing: TYPE_INTERVAL_MS,
      holding: HOLD_MS,
      deleting: DELETE_INTERVAL_MS,
      empty: EMPTY_HOLD_MS,
    }[phase];

    const timer = window.setTimeout(() => {
      if (phase === 'typing') {
        if (shownCount < current.length) setShownCount(shownCount + 1);
        else setPhase('holding');
      } else if (phase === 'holding') {
        setPhase('deleting');
      } else if (phase === 'deleting') {
        if (shownCount > 0) setShownCount(shownCount - 1);
        else setPhase('empty');
      } else {
        setSampleIndex((sampleIndex + 1) % graphemeSamples.length);
        setPhase('typing');
      }
    }, delay);
    return () => window.clearTimeout(timer);
  }, [isAnimated, graphemeSamples, sampleIndex, shownCount, phase]);

  const text = isAnimated
    ? (graphemeSamples[sampleIndex] ?? []).slice(0, shownCount).join('')
    : samples[0] ?? '';

  return { ref, text };
};
